import { Appender } from "./Appender";
import { BrowserConsoleAppender } from "./appender/BrowserConsoleAppender";
import { LoggerNameWithMessageLayout } from "./layout/LoggerNameWithMessageLayout";
import { Logger } from "./Logger";
import { LoggingLevel } from "./LoggingLevel";

interface LoggerFactoryConfig {
  /**
   * Конфигурации логгеров.
   *
   * При создании логгера берется последняя подходящая конфигурация.
   *
   * Выбор конфигурации: совпадает ли имя логгера с регулярным выражением {@link LoggerConfig.pattern}.
   */
  configs: LoggerConfig[];
}

interface LoggerConfig {
  /**
   * Уровень логирования.
   */
  level: LoggingLevel;

  /**
   * Регулярное выражение, по которому происходит проверка имени логгера.
   */
  pattern: RegExp;

  /**
   * Обработчиков лог событий, которые будут использоваться логгером.
   */
  appenders: Appender[];
}

/**
 * Конфигурация по умолчанию.
 *
 * Уровень логирования INFO и вывод лог событий будет производиться в консоль браузера.
 */
const INITIAL_CONFIG: LoggerFactoryConfig = {
  configs: [
    {
      pattern: new RegExp("."),
      level: LoggingLevel.INFO,
      appenders: [
        new BrowserConsoleAppender(new LoggerNameWithMessageLayout()),
      ],
    },
  ],
};

/**
 * Создает функцию для получения логгеров.
 * @param config - конфигурация, по умолчанию {@link INITIAL_CONFIG}
 */
export function getLoggerFactory(config: LoggerFactoryConfig = INITIAL_CONFIG) {
  const cachedLoggers = new Map<string, Logger>();

  return (name: string) => {
    if (cachedLoggers.has(name)) {
      return cachedLoggers.get(name);
    } else {
      return createLogger(config, name);
    }
  };
}

function createLogger(config: LoggerFactoryConfig, loggerName: string): Logger {
  const loggerConfig = getLoggerConfig(config, loggerName);

  return new Logger(loggerName, loggerConfig.appenders, loggerConfig.level);
}

function getLoggerConfig(config: LoggerFactoryConfig, loggerName: string) {
  const loggerConfig = [...config.configs]
    .reverse()
    .find((config) => config.pattern.test(loggerName));

  if (loggerConfig) {
    return loggerConfig;
  } else {
    throw new Error(`There isn't config for logger with name=${loggerName}`);
  }
}
